import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Clipboard, Mail, RefreshCw } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { api } from "@/lib/api";
import { formatAvailabilityDate } from "@/lib/availability";
import "@/styles/brand-a.css";
import "@/styles/admin-invites.css";
import "@/styles/phase3-loops.css";

type InviteStatus = "pending" | "used" | "expired";

interface PracticeGroup {
  id: string;
  name: string;
  slug: string;
  contactEmail: string | null;
  contactPhone: string | null;
  facilityCount: number;
  confirmedCount: number;
  latestConfirmedAt: string | null;
}

interface OnboardingInvite {
  id: string;
  groupName: string | null;
  facilityName: string | null;
  email: string;
  sentAt: string | null;
  usedAt: string | null;
  expiresAt: string;
}

interface InviteResponse {
  id: string;
  expiresAt: string;
  url?: string;
}

interface ColleagueInviteStats {
  sent7d: number;
  subscribed7d: number;
  sentTotal: number;
  subscribedTotal: number;
}

export default function AdminInvites() {
  const [status, setStatus] = useState<InviteStatus>("pending");
  const groupsQuery = useQuery({
    queryKey: ["admin-practice-groups"],
    queryFn: () => api.get<PracticeGroup[]>("/v1/admin/practice-groups"),
    retry: false,
  });
  const invitesQuery = useQuery({
    queryKey: ["admin-onboarding-invites", status],
    queryFn: () =>
      api.get<OnboardingInvite[]>("/v1/admin/onboarding-invites", {
        params: { status },
      }),
    retry: false,
  });
  const colleagueStats = useQuery({
    queryKey: ["admin-colleague-invite-stats"],
    queryFn: () => api.get<ColleagueInviteStats>("/v1/admin/invites/stats"),
    retry: false,
  });

  return (
    <div className="brand-a pb-admin-invites-page">
      <main className="pb-admin-invites-main">
        <AdminNav />

        <header className="pb-admin-heading">
          <div>
            <p>Admin · Availability</p>
            <h1>Practice claim links</h1>
          </div>
          <button
            type="button"
            onClick={() => {
              void groupsQuery.refetch();
              void invitesQuery.refetch();
              void colleagueStats.refetch();
            }}
          >
            <RefreshCw size={15} aria-hidden="true" /> Refresh
          </button>
        </header>

        {colleagueStats.isLoading ? <AdminLoading label="Loading colleague invite stats…" /> : null}
        {colleagueStats.isError ? (
          <AdminError message="Colleague invite stats could not be loaded." />
        ) : null}
        {colleagueStats.data ? (
          <section className="pb-admin-invite-stats" aria-label="Colleague invite statistics">
            <div><strong>{colleagueStats.data.sent7d}</strong><span>Sent · 7 days</span></div>
            <div><strong>{colleagueStats.data.subscribed7d}</strong><span>Subscribed · 7 days</span></div>
            <div><strong>{colleagueStats.data.sentTotal}</strong><span>Sent · total</span></div>
            <div><strong>{colleagueStats.data.subscribedTotal}</strong><span>Subscribed · total</span></div>
          </section>
        ) : null}

        <section aria-labelledby="practice-groups-heading">
          <div className="pb-admin-section-heading">
            <h2 id="practice-groups-heading">Practice groups</h2>
            <p>Edit the contact email before creating a link.</p>
          </div>

          {groupsQuery.isLoading ? <AdminLoading label="Loading practice groups…" /> : null}
          {groupsQuery.isError ? (
            <AdminError message="Practice groups could not be loaded. Check your admin access and try again." />
          ) : null}
          {groupsQuery.data?.length === 0 ? (
            <AdminEmpty message="No practice groups are available." />
          ) : null}

          {groupsQuery.data && groupsQuery.data.length > 0 ? (
            <div className="pb-admin-table-wrap">
              <table className="pb-admin-table">
                <thead>
                  <tr>
                    <th>Practice</th>
                    <th>Doctors</th>
                    <th>Confirmed</th>
                    <th>Latest confirmed</th>
                    <th>Contact email</th>
                    <th>Claim link</th>
                  </tr>
                </thead>
                <tbody>
                  {groupsQuery.data.map((group) => (
                    <PracticeGroupRow key={group.id} group={group} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>

        <section className="pb-admin-invite-list" aria-labelledby="invites-heading">
          <div className="pb-admin-section-heading pb-admin-section-heading--tabs">
            <div>
              <h2 id="invites-heading">Invites</h2>
              <p>Sent and generated claim links by current status.</p>
            </div>
            <div className="pb-admin-tabs" aria-label="Invite status">
              {(["pending", "used", "expired"] as InviteStatus[]).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={status === value}
                  onClick={() => setStatus(value)}
                >
                  {value.charAt(0).toUpperCase() + value.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {invitesQuery.isLoading ? <AdminLoading label="Loading invites…" /> : null}
          {invitesQuery.isError ? (
            <AdminError message="Invites could not be loaded. Try again." />
          ) : null}
          {invitesQuery.data?.length === 0 ? (
            <AdminEmpty message={`No ${status} invites.`} />
          ) : null}

          {invitesQuery.data && invitesQuery.data.length > 0 ? (
            <div className="pb-admin-table-wrap">
              <table className="pb-admin-table pb-admin-table--invites">
                <thead>
                  <tr>
                    <th>Practice or doctor</th>
                    <th>Email</th>
                    <th>Sent</th>
                    <th>Used</th>
                    <th>Expires</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invitesQuery.data.map((invite) => (
                    <tr key={invite.id}>
                      <td>{invite.groupName ?? invite.facilityName ?? "Invite scope unavailable"}</td>
                      <td>{invite.email}</td>
                      <td>{formatOptionalDate(invite.sentAt)}</td>
                      <td>{formatOptionalDate(invite.usedAt)}</td>
                      <td>{formatAvailabilityDate(invite.expiresAt)}</td>
                      <td>
                        <span className="pb-admin-status" data-status={status}>
                          {status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      </main>
    </div>
  );
}

function PracticeGroupRow({ group }: { group: PracticeGroup }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(group.contactEmail ?? "");
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => setEmail(group.contactEmail ?? ""), [group.contactEmail]);

  const updateContact = useMutation({
    mutationFn: () =>
      api.patch(`/v1/admin/practice-groups/${group.id}`, {
        contactEmail: email.trim(),
      }),
    onSuccess: () => {
      setNotice("Contact saved.");
      void queryClient.invalidateQueries({ queryKey: ["admin-practice-groups"] });
    },
    onError: (error) => setNotice(errorNotice(error)),
  });

  const sendInvite = useMutation({
    mutationFn: () =>
      api.post<InviteResponse>("/v1/admin/onboarding-invites", {
        practiceGroupId: group.id,
        email: email.trim(),
        ...(group.contactPhone ? { phone: group.contactPhone } : {}),
        kind: "claim",
        send: true,
      }),
    onSuccess: () => {
      setNotice("Claim link sent.");
      void queryClient.invalidateQueries({ queryKey: ["admin-onboarding-invites"] });
    },
    onError: (error) => setNotice(errorNotice(error)),
  });

  const createLink = useMutation({
    mutationFn: () =>
      api.post<InviteResponse>("/v1/admin/onboarding-invites", {
        practiceGroupId: group.id,
        email: email.trim(),
        ...(group.contactPhone ? { phone: group.contactPhone } : {}),
        kind: "claim",
        send: false,
      }),
    onSuccess: (response) => {
      if (!response.url) {
        setNotice("The API did not return a copyable link.");
        return;
      }
      setGeneratedUrl(response.url);
      void copyText(response.url).then((copied) => {
        setNotice(copied ? "Link created and copied." : "Link created. Copy it below.");
      });
      void queryClient.invalidateQueries({ queryKey: ["admin-onboarding-invites"] });
    },
    onError: (error) => setNotice(errorNotice(error)),
  });

  const hasEmail = email.trim().length > 0;
  const busy = updateContact.isPending || sendInvite.isPending || createLink.isPending;

  return (
    <tr>
      <td>
        <strong>{group.name}</strong>
      </td>
      <td>{group.facilityCount}</td>
      <td>
        {group.confirmedCount}/{group.facilityCount}
      </td>
      <td>{formatOptionalDate(group.latestConfirmedAt)}</td>
      <td className="pb-admin-email-cell">
        <input
          type="email"
          aria-label={`Contact email for ${group.name}`}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setNotice(null);
          }}
          placeholder="practice@example.com.au"
        />
        <button
          type="button"
          disabled={!hasEmail || busy || email.trim() === (group.contactEmail ?? "")}
          onClick={() => updateContact.mutate()}
        >
          Save
        </button>
      </td>
      <td className="pb-admin-actions-cell">
        <div>
          <button
            type="button"
            disabled={!hasEmail || busy}
            onClick={() => {
              setNotice(null);
              sendInvite.mutate();
            }}
          >
            <Mail size={14} aria-hidden="true" /> Send claim link
          </button>
          <button
            type="button"
            disabled={!hasEmail || busy}
            onClick={() => {
              setNotice(null);
              if (generatedUrl) {
                void copyText(generatedUrl).then((copied) =>
                  setNotice(copied ? "Link copied." : "Copy the link shown below."),
                );
              } else {
                createLink.mutate();
              }
            }}
          >
            {generatedUrl ? <Check size={14} aria-hidden="true" /> : <Clipboard size={14} aria-hidden="true" />}
            {generatedUrl ? "Copy shown link" : "Copy link"}
          </button>
        </div>
        {generatedUrl ? (
          <output className="pb-admin-generated-url" aria-label="Generated claim URL">
            {generatedUrl}
          </output>
        ) : null}
        {notice ? <p className="pb-admin-row-notice">{notice}</p> : null}
      </td>
    </tr>
  );
}

function AdminLoading({ label }: { label: string }) {
  return <p className="pb-admin-state">{label}</p>;
}

function AdminError({ message }: { message: string }) {
  return (
    <p className="pb-admin-state pb-admin-state--error" role="alert">
      {message}
    </p>
  );
}

function AdminEmpty({ message }: { message: string }) {
  return <p className="pb-admin-state">{message}</p>;
}

function formatOptionalDate(value: string | null): string {
  return value ? formatAvailabilityDate(value) : "—";
}

function errorNotice(error: unknown): string {
  return error instanceof Error && error.message ? error.message : "Action failed. Try again.";
}

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}
